import { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getPublicForm, type FormTypePrefix } from '../lib/api';
import FormRenderer from '../components/renderer/FormRenderer';
import type { Form } from '../types/form';
import { SkeletonFormPreview } from '../components/ui/Skeleton';
import { useSEO, useStructuredData, generateFormStructuredData, SITE_CONFIG } from '../lib/seo';
import FormAccessGate from '../components/form/FormAccessGate';

// Extract form type prefix from URL path
function getFormTypeFromPath(pathname: string): FormTypePrefix {
  if (pathname.startsWith('/p/')) return 'p';
  if (pathname.startsWith('/q/')) return 'q';
  if (pathname.startsWith('/l/')) return 'l';
  return 'f'; // default
}

export default function PublicFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [liveQuizInfo, setLiveQuizInfo] = useState<{ gamePin?: string; message?: string } | null>(null);
  const [accessGranted, setAccessGranted] = useState(false);

  // SEO: Dynamic meta tags for the form
  useSEO({
    title: form?.name || 'Form',
    description: form?.description || `Fill out this ${form?.form_type || 'form'} on ${SITE_CONFIG.name}`,
    url: `${SITE_CONFIG.domain}${location.pathname}`,
    type: 'website',
  });

  // SEO: Generate structured data for the form
  const structuredData = useMemo(() => {
    if (!form) return null;
    return generateFormStructuredData({
      name: form.name || 'Form',
      description: form.description || undefined,
      type: form.form_type,
      url: `${SITE_CONFIG.domain}${location.pathname}`,
      dateCreated: form.created_at,
      dateModified: form.updated_at,
    });
  }, [form, location.pathname]);

  useStructuredData(structuredData, 'form-structured-data');

  // Set custom favicon if provided
  useEffect(() => {
    if (form?.schema.settings.favicon) {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'icon';
      link.href = form.schema.settings.favicon;
      if (!document.querySelector("link[rel~='icon']")) {
        document.head.appendChild(link);
      }
    }
  }, [form?.schema.settings.favicon]);

  // Check for existing access token on mount
  useEffect(() => {
    if (form?.id) {
      const accessToken = localStorage.getItem(`form_access_${form.id}`);
      if (accessToken) {
        setAccessGranted(true);
      }
    }
  }, [form?.id]);

  useEffect(() => {
    if (!slug) return;

    async function loadForm() {
      try {
        setLoading(true);
        const typePrefix = getFormTypeFromPath(location.pathname);
        const formData = await getPublicForm(slug!, typePrefix);

        // Handle live quiz special case
        if (typePrefix === 'l' && formData.form_type === 'live_quiz') {
          setLiveQuizInfo({
            gamePin: (formData.activeSession as { game_pin?: string })?.game_pin,
            message: (formData as { message?: string }).message,
          });
        }

        setForm(formData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Form not found';
        // Handle redirect response
        if (errorMessage.startsWith('REDIRECT:')) {
          const redirectPath = errorMessage.replace('REDIRECT:', '');
          navigate(redirectPath, { replace: true });
          return;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }

    loadForm();
  }, [slug, location.pathname, navigate]);

  function handleSuccess(result: { submissionId: string; message: string; redirectUrl?: string }) {
    setSubmitted(true);
    setSuccessMessage(result.message);
  }

  function handleError(err: Error) {
    setError(err.message);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4">
        <div className="max-w-xl mx-auto bg-white rounded-xl shadow-sm">
          <SkeletonFormPreview />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const isDark = form?.schema.settings.theme === 'dark';

    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <div className={`text-center max-w-md p-8 rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Submission Received!
          </h1>
          <p className={isDark ? 'text-gray-300' : 'text-gray-500'}>
            {successMessage}
          </p>
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  // Check if form has access control enabled
  const hasAccessControl = form.schema.settings.accessControl?.enabled;
  const needsAccess = hasAccessControl && !accessGranted;

  // Show access gate if access control is enabled and user hasn't been granted access
  if (needsAccess && form.schema.settings.accessControl) {
    return (
      <FormAccessGate
        formId={form.id}
        accessControl={form.schema.settings.accessControl}
        onAccessGranted={() => setAccessGranted(true)}
        isDark={form.schema.settings.theme === 'dark'}
      />
    );
  }

  // Special handling for live quizzes - show join info instead of form
  if (liveQuizInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-6">🎮</div>
          <h1 className="text-3xl font-bold text-white mb-4">{form.name}</h1>
          {form.description && (
            <p className="text-purple-300 mb-6">{form.description}</p>
          )}

          {liveQuizInfo.gamePin ? (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-6">
              <p className="text-purple-200 text-sm mb-2">Game PIN</p>
              <div className="text-5xl font-mono font-bold text-white tracking-widest">
                {liveQuizInfo.gamePin}
              </div>
            </div>
          ) : (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-purple-200">{liveQuizInfo.message}</p>
            </div>
          )}

          <button
            onClick={() => navigate(liveQuizInfo.gamePin ? `/play?pin=${liveQuizInfo.gamePin}` : '/play')}
            className="px-8 py-4 bg-white text-purple-700 font-bold text-lg rounded-xl hover:bg-purple-100 transition-colors"
          >
            {liveQuizInfo.gamePin ? 'Join Game' : 'Go to Join Page'}
          </button>
        </div>
      </div>
    );
  }

  const isDark = form.schema.settings.theme === 'dark';
  const direction = form.schema.settings.direction || 'ltr';

  return (
    <div className={`min-h-screen py-12 px-4 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`} dir={direction}>
      <div className="max-w-xl mx-auto">
        {/* Form Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          {form.schema.settings.logo && (
            <div className="mb-6 flex justify-center">
              <img
                src={form.schema.settings.logo}
                alt="Form logo"
                className="max-h-24 w-auto object-contain"
              />
            </div>
          )}

          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {form.name}
          </h1>
          {form.schema.settings.subtitle && (
            <p className={`mt-1 text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {form.schema.settings.subtitle}
            </p>
          )}
          {form.description && (
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {form.description}
            </p>
          )}
          {form.schema.settings.instructions && (
            <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-100'}`}>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap`}>
                {form.schema.settings.instructions}
              </p>
            </div>
          )}
        </div>

        {/* Form */}
        <FormRenderer
          formId={form.id}
          schema={form.schema}
          onSuccess={handleSuccess}
          onError={handleError}
        />

        {/* Footer */}
        <div className="text-center mt-8">
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Powered by xt4m.com
          </p>
        </div>
      </div>
    </div>
  );
}
